import { forwardRef, type HTMLAttributes, type TableHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

/**
 * Loading placeholders. DS-48 keeps motion to state changes only, so the
 * shimmer is the single `animate-pulse` tint rather than a travelling gradient.
 */

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Number = px, string = any CSS length ('60%', '8rem'). Defaults to full width. */
  w?: number | string;
  /** Number = px, string = any CSS length. Defaults to one dense line (12px). */
  h?: number | string;
  /** `true` → `rounded`, `'full'` → pill/avatar, `false` → square corners. */
  rounded?: boolean | 'full';
}

function length(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ w, h = 12, rounded = true, className, style, ...props }, ref) => (
    <div
      ref={ref}
      aria-hidden
      className={cn(
        'animate-pulse bg-slate-200',
        w === undefined && 'w-full',
        rounded === 'full' ? 'rounded-full' : rounded && 'rounded',
        className,
      )}
      style={{ width: length(w), height: length(h), ...style }}
      {...props}
    />
  ),
);
Skeleton.displayName = 'Skeleton';

export interface SkeletonTableProps extends TableHTMLAttributes<HTMLTableElement> {
  rows?: number;
  cols?: number;
  /** Render the `.th-base` header strip above the shimmer rows. */
  header?: boolean;
}

/** Uneven widths read as data rather than as a grid of identical bars. */
const CELL_WIDTHS = ['70%', '45%', '85%', '55%', '35%', '65%'];

/**
 * DS-20…25 — the loading state of a table has to occupy exactly the space the
 * real table will, or the screen jumps when the query lands. Same `p-2.5`
 * cells, same header, same row borders.
 */
export const SkeletonTable = forwardRef<HTMLTableElement, SkeletonTableProps>(
  ({ rows = 8, cols = 5, header = true, className, ...props }, ref) => (
    <table ref={ref} className={cn('w-full text-xs', className)} {...props}>
      {header ? (
        <thead>
          <tr>
            {Array.from({ length: cols }, (_, c) => (
              <th key={c} className="th-base">
                <Skeleton h={8} w="50%" />
              </th>
            ))}
          </tr>
        </thead>
      ) : null}
      <tbody>
        {Array.from({ length: rows }, (_, r) => (
          <tr key={r}>
            {Array.from({ length: cols }, (_, c) => (
              <td key={c} className="td-base">
                <Skeleton h={10} w={CELL_WIDTHS[(r + c) % CELL_WIDTHS.length]} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  ),
);
SkeletonTable.displayName = 'SkeletonTable';
